import { useRef } from "react";

import emailjs from "@emailjs/browser";

import {
  User,
  Mail,
  FileText,
  MessageSquare,
  Send,
  Lock,
  MapPin,
  Phone,
  ArrowUpRight,
} from "lucide-react";

import { FaInstagram } from "react-icons/fa";

import "../styles/contact.css";

const Contact = () => {

  const form = useRef<HTMLFormElement>(null);

  const sendEmail = (
    e: React.FormEvent
  ) => {

    e.preventDefault();

    if (!form.current) return;

    emailjs.sendForm(
      "service_mwmvtag",
      "template_3nle1we",
      form.current,
      "CP_boyHtuFcqQoFj-"
    )
    .then(() => {

      alert("Message sent successfully!");

      form.current?.reset();

    })
    .catch((error) => {

      console.log(error);

      alert("Failed to send message.");

    });

  };

  return (
    <section id="contact" className="contact">

      {/* =====================================
          BACKGROUND
      ===================================== */}

      <div className="contact-orb contact-orb-one"></div>
      <div className="contact-orb contact-orb-two"></div>


      {/* =====================================
          HEADER
      ===================================== */}

      <div className="contact-header">

        <div className="contact-eyebrow">
          <span></span>
          CONTACT US
          <span></span>
        </div>

        <h2>
          Let's build something
          <br />
          <em>smarter together.</em>
        </h2>

        <p>
          Have a question, partnership idea or vending enquiry?
          <br className="desktop-break" />
          We'd love to hear from you.
        </p>

      </div>


      {/* =====================================
          MAIN CONTACT AREA
      ===================================== */}

      <div className="contact-wrapper">


        {/* =================================
            CONTACT FORM
        ================================= */}

        <div className="contact-form-card">

          <div className="form-top">

            <div>

              <span className="form-label">
                START A CONVERSATION
              </span>

              <h3>
                Bring Protein
                <br />
                Vending To Your Gym.
              </h3>

            </div>

            <div className="form-number">
              01
            </div>

          </div>


          <form
            ref={form}
            onSubmit={sendEmail}
            className="contact-form"
          >


            {/* NAME + EMAIL */}

            <div className="form-row">

              {/* NAME */}

              <div className="field">

                <label htmlFor="user_name">
                  Your Name
                </label>

                <div className="input-wrapper">

                  <User size={18} />

                  <input
                    id="user_name"
                    type="text"
                    name="user_name"
                    placeholder="Your name"
                    required
                  />

                </div>

              </div>


              {/* EMAIL */}

              <div className="field">

                <label htmlFor="user_email">
                  Your Email
                </label>

                <div className="input-wrapper">

                  <Mail size={18} />

                  <input
                    id="user_email"
                    type="email"
                    name="user_email"
                    placeholder="your@email.com"
                    required
                  />

                </div>

              </div>

            </div>


            {/* SUBJECT */}

            <div className="field">

              <label htmlFor="subject">
                Subject
              </label>

              <div className="input-wrapper">

                <FileText size={18} />

                <input
                  id="subject"
                  type="text"
                  name="subject"
                  placeholder="What can we help you with?"
                  required
                />

              </div>

            </div>


            {/* MESSAGE */}

            <div className="field">

              <label htmlFor="message">
                Your Message
              </label>

              <div className="textarea-wrapper">

                <MessageSquare size={18} />

                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us a little about your project..."
                  required
                />

              </div>

            </div>


            {/* SUBMIT */}

            <button
              type="submit"
              className="contact-submit"
            >

              <span>
                Send Message
              </span>

              <span className="submit-icon">
                <Send size={18} />
              </span>

            </button>


            {/* SECURITY */}

            <div className="form-security">

              <Lock size={14} />

              <span>
                Your information is safe and secure.
              </span>

            </div>

          </form>

        </div>


        {/* =================================
            RIGHT SIDE
        ================================= */}

        <div className="contact-info">


          {/* INFO HEADING */}

          <div className="info-heading">

            <span className="mini-contact-tag">
              GET IN TOUCH
            </span>

            <h3>
              Smart nutrition
              <br />
              starts <span>here.</span>
            </h3>

            <div className="heading-line"></div>

            <p>
              Connect with Dagda Nutrition to explore
              smart protein vending solutions for gyms,
              offices, campuses and wellness environments.
            </p>

          </div>


          {/* =================================
              CONTACT CARDS
          ================================= */}

          <div className="contact-info-grid">


            {/* LOCATION */}

            <div className="info-box">

              <div className="info-icon">

                <MapPin size={20} />

              </div>

              <div className="info-content">

                <span>
                  LOCATION
                </span>

                <h4>
                  Head Office
                </h4>

                <p>
                  Dublin, Ireland
                </p>

              </div>

            </div>


            {/* PHONE */}

            <a
              href="tel:+353892753452"
              className="info-box info-link"
            >

              <div className="info-icon">

                <Phone size={20} />

              </div>

              <div className="info-content">

                <span>
                  PHONE
                </span>

                <h4>
                  Call Support
                </h4>

                <p>
                  +353 89 2753452
                </p>

              </div>

              <ArrowUpRight
                className="card-arrow"
                size={18}
              />

            </a>


            {/* EMAIL */}

            <a
              href="mailto:hello@dagdanutrition.com"
              className="info-box info-link"
            >

              <div className="info-icon">

                <Mail size={20} />

              </div>

              <div className="info-content">

                <span>
                  EMAIL
                </span>

                <h4>
                  Email Us
                </h4>

                <p>
                  hello@dagdanutrition.com
                </p>

              </div>

              <ArrowUpRight
                className="card-arrow"
                size={18}
              />

            </a>


            {/* INSTAGRAM */}

            <a
              href="https://www.instagram.com/dagdanutrition/"
              target="_blank"
              rel="noopener noreferrer"
              className="info-box info-link instagram-box"
              aria-label="Visit Dagda Nutrition on Instagram"
            >

              <div className="info-icon instagram-icon">

                <FaInstagram size={20} />

              </div>

              <div className="info-content">

                <span>
                  SOCIAL
                </span>

                <h4>
                  Instagram
                </h4>

                <p>
                  dagdanutrition
                </p>

              </div>

              <ArrowUpRight
                className="card-arrow"
                size={18}
              />

            </a>

          </div>

        </div>

      </div>


      {/* =====================================
          MAP
      ===================================== */}

      <div className="map-section">

        <div className="map-header">

          <div className="map-header-icon">
            <MapPin size={18} />
          </div>

          <div>

            <span>
              FIND US
            </span>

            <p>
              Dublin, Ireland
            </p>

          </div>

        </div>


        <iframe
          title="Dagda Nutrition Dublin location"
          src="https://maps.google.com/maps?q=Dublin%20Ireland&t=&z=12&ie=UTF8&iwloc=&output=embed"
          loading="lazy"
          allowFullScreen
        ></iframe>

      </div>

    </section>
  );
};

export default Contact;